import { useEffect, useState } from 'react'
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, writeBatch } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { rrmDeferredTasks } from '@/data/rrmDeferredTasks'

export type DeferredStatus = 'captured' | 'triaged' | 'ready' | 'scheduled' | 'in_progress' | 'validation' | 'released' | 'verified' | 'closed' | 'blocked' | 'deferred'
export interface DeferredTask { id: string; source_key: string; title: string; application: string; repository: string; priority: 'P0'|'P1'|'P2'|'P3'; status: DeferredStatus; summary: string; window_class: string; firebase_project?: string; firestore_database?: string; hosting_site?: string; updated_at?: unknown }

export interface ManualDeferredTaskInput {
  title: string
  application: string
  repository: string
  firebase_project: string
  firestore_database: string
  hosting_site: string
  priority: 'P0'|'P1'|'P2'|'P3'
  platform_scope: 'internal'|'external_saas'|'shared_control_plane'|'portfolio_operations'
  window_class: 'immediate_containment'|'standard_low_traffic'|'announced_maintenance'|'major_release'
  summary: string
  acceptance_criteria: string[]
}

const stableId = (key: string) => key.replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 180)

export function useDeferredTasks() {
  const [items, setItems] = useState<DeferredTask[]>([])
  const [loading, setLoading] = useState(true)
  useEffect(() => onSnapshot(query(collection(db, 'deferred_changes'), orderBy('priority')), snap => {
    setItems(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<DeferredTask, 'id'>) })))
    setLoading(false)
  }, () => setLoading(false)), [])
  return { items, loading }
}

export async function seedRrmDeferredTasks(actorEmail: string) {
  const batch = writeBatch(db)
  for (const [source_key, title, priority, summary] of rrmDeferredTasks) {
    const id = stableId(source_key)
    batch.set(doc(db, 'deferred_changes', id), {
      source_key, title, priority, summary,
      application: 'Reminiscent Chronicles SaaS', entity: 'Reminiscent Road Media LLC',
      repository: 'rrm-studio/ReminiscentChronicles', platform_scope: 'external_saas',
      status: 'captured', window_class: priority === 'P0' ? 'announced_maintenance' : 'standard_low_traffic',
      acceptance_criteria: ['Authorized positive test passes', 'Cross-tenant negative test passes', 'CI and deployment evidence attached', 'Production smoke test passes'],
      communication_plan: 'Assess tenant impact; send advance, start, progress, completion, and incident notices when applicable.',
      rollback_plan: 'Document repository-specific rollback before scheduling.', updated_at: serverTimestamp(),
      created_at: serverTimestamp(), created_by: actorEmail,
    }, { merge: true })
    batch.set(doc(collection(db, 'audit_log')), { action: 'deferred_change.seeded', target_id: id, source_key, actor_email: actorEmail, timestamp: serverTimestamp() })
  }
  await batch.commit()
}

export async function updateDeferredStatus(task: DeferredTask, status: DeferredStatus, actorEmail: string) {
  const batch = writeBatch(db)
  batch.update(doc(db, 'deferred_changes', task.id), { status, updated_at: serverTimestamp(), last_updated_by: actorEmail })
  batch.set(doc(collection(db, 'audit_log')), { action: 'deferred_change.status_updated', target_id: task.id, from: task.status, to: status, actor_email: actorEmail, timestamp: serverTimestamp() })
  await batch.commit()
}

export async function createManualDeferredTask(input: ManualDeferredTaskInput, actorEmail: string) {
  const id = crypto.randomUUID()
  const source_key = `manual:${input.firebase_project || 'portfolio'}:${id}`
  const batch = writeBatch(db)
  batch.set(doc(db, 'deferred_changes', id), {
    ...input, source_key, status: 'captured', entity: 'Ascendant Road Holdings LLC',
    acceptance_criteria: input.acceptance_criteria.length ? input.acceptance_criteria : ['Owner verifies the intended result in production'],
    communication_plan: 'Assess who may be affected before scheduling. Send advance, start, completion, and incident notices when applicable.',
    rollback_plan: 'The assigned implementer must document a safe rollback before moving this task to scheduled.',
    created_at: serverTimestamp(), updated_at: serverTimestamp(), created_by: actorEmail,
  })
  batch.set(doc(collection(db, 'audit_log')), {
    action: 'deferred_change.created', target_id: id, source_key, actor_email: actorEmail,
    firebase_project: input.firebase_project, hosting_site: input.hosting_site, timestamp: serverTimestamp(),
  })
  await batch.commit()
}
